import React, { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { useSetRecoilState } from 'recoil';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import usePrivileges from '@ubeya/shared/hooks/generic/usePrivileges';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import privileges from '@ubeya/shared-web/privileges';
import { openHelpCenter } from '@ubeya/shared/atoms/shared';
import {
  DropDown,
  FlexMiddle,
  Avatar as BaseAvatar,
  SmallBodySemiBold,
  Header3SemiBold,
  FlexColumn,
  Small,
  flexCenter
} from '@ubeya/shared-web/components';
import { ReactComponent as BaseProductUpdatesIcon } from '@ubeya/shared-web/assets/TopbarIcons/product-update.svg';
import { ReactComponent as BaseHelpCenterIcon } from '@ubeya/shared-web/assets/TopbarIcons/help-center.svg';
import { ReactComponent as BaseSupportChatIcon } from '@ubeya/shared-web/assets/chat-support.svg';
import { ReactComponent as LogoutIcon } from '@ubeya/shared-web/assets/logout.svg';
import { ReactComponent as PurpleLogo } from '@ubeya/shared-web/assets/logo-purple.svg';
import { ReactComponent as NotificationSettingsIcon } from '@ubeya/shared-web/assets/SidebarIcons/Submodules/settings-notification.svg';
import { ReactComponent as VSign } from '@ubeya/shared-web/assets/tick.svg';
import { media } from '@ubeya/shared-web/style';
import { isTablet } from 'react-device-detect';
import { SubmoduleIcons } from '@ubeya/shared-web/assets/SidebarIcons';
import { useHistory } from 'react-router-dom';
import useAccountAdminConfig from '@ubeya/shared/hooks/account/useAccountAdminConfig';
import { useAccountUrl } from '../AccountRouter';
import ProfileModal from './ProfileModal';

const Wrapper = styled.div``;

const AccountName = styled(Header3SemiBold)`
  margin-right: 10px;
`;

const Avatar = styled(BaseAvatar)`
  margin-right: 5px;
`;

const baseIconStyles = css`
  width: 19px;
  height: 19px;
`;

const SupportChatIcon = styled(BaseSupportChatIcon)`
  ${baseIconStyles}
`;

const HelpCenterIcon = styled(BaseHelpCenterIcon)`
  ${baseIconStyles}
`;

const ProductUpdatesIcon = styled(BaseProductUpdatesIcon)`
  ${baseIconStyles}
`;

const Accounts = styled.div`
  margin: -10px;
  padding: 10px;
  max-height: 30vh;
  overflow: auto;
`;

const AccountsContainer = styled(FlexMiddle)`
  padding: 6px 8px 6px 14px;
  width: 100%;
  border-radius: 4px;
  background-color: ${({ theme, isSelected }) => (isSelected ? theme.colors.grey2 : 'transparent')};
`;

const Privileges = styled(Small)`
  display: inline;
`;

const getMyPrivilegeType = (myPrivileges, mappedPrivileges) => {
  if (myPrivileges[privileges.ACCOUNT_ADMIN]) {
    return mappedPrivileges[privileges.ACCOUNT_ADMIN];
  }

  if (myPrivileges[privileges.BRANCH_ADMIN]) {
    return mappedPrivileges[privileges.BRANCH_ADMIN];
  }

  if (myPrivileges[privileges.MANAGER_ADMIN]) {
    return mappedPrivileges[privileges.MANAGER_ADMIN];
  }

  return '';
};

const Image = styled.div.attrs(({ image }) => ({ className: 'avatar', style: { backgroundImage: `url(${image})` } }))`
  ${({ image, isAccount }) =>
    image
      ? css`
          border: 1px solid ${({ theme }) => theme.colors.white};
          background: ${({ theme }) => theme.colors.disabled};
        `
      : isAccount
      ? css`
          background: ${({ theme }) => theme.colors.generalBlue};
        `
      : css`
          background: ${({ theme }) => theme.colors.primary};
        `};

  position: relative;
  ${flexCenter};
  border-radius: 100%;
  width: 32px;
  height: 32px;
  min-width: 24px;
  min-height: 24px;

  background-size: cover;
  background-position: center;
  color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.white};
  outline: 2px solid ${({ image, theme }) => (image ? 'transparent' : theme.colors.grey2)};
  font-size: 13px;
  line-height: 22px;
  letter-spacing: 1px;
  text-align: center;
  font-weight: 500;

  > svg {
    width: 16px;
    height: 16px;
    ${media.tablet`width: 20px;height: 20px;`}
  }

  ${media.tablet`
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
  `}
`;

const Popover = styled.div`
  box-shadow: 3px 3px 7px 5px rgba(34, 36, 111, 0.15);
  position: absolute;
  left: 100%;
  bottom: 4px;
  z-index: 3;
  background-color: ${({ theme }) => theme.colors.primary};
  height: 50px;
  border-radius: 0 40px 40px 0;
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 5px;
  cursor: pointer;
  transition: all ease-in-out 0.15s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PoweredByText = styled.p`
  color: ${({ theme }) => theme.colors.grey1};
  white-space: nowrap;
  font-weight: lighter;
  text-decoration: none;
  margin-left: 5px;
`;

const ProfileMenuAvatar = ({ className, firstName, lastName, image, isAccount = false }) => {
  const innerData = image ? (
    <></>
  ) : firstName || lastName ? (
    [firstName?.charAt?.(0), lastName?.charAt?.(0)].join('')
  ) : (
    'N / A'
  );

  return (
    <Image image={image} className={className} isAccount={isAccount}>
      {innerData}
    </Image>
  );
};

const ProfileMenu = ({ isMobile = false, isSideBar = false, type, className }) => {
  const { t, isAccountNetworkAccessSupplier, isHost } = useContext(ConfigContext);

  const [isHostProfileModal, setIsHostProfileModal] = useState(false);

  const {
    data: { adminId, firstName, lastName, image, email, accounts = [] },
    logout,
    isUbeyaAdmin,
    ubeyaAdminViewAsClient,
    setUbeyaAdminViewAsClient
  } = useUser();
  const { accountId, account } = useAccount();
  const { isRtl } = useContext(ConfigContext);
  const { push } = useHistory();
  const billingTo = useAccountUrl({ to: '/:accountId/billing' });
  const { adminConfigData, updateAdminConfig } = useAccountAdminConfig({ accountId, adminId, isUbeyaAdmin });

  const setOpenHelpCenter = useSetRecoilState(openHelpCenter);

  const primaryAvatar =
    type === 'both' || type === 'admin'
      ? {
          firstName: account?.name?.split(' ')?.[0] || '',
          lastName: account?.name?.split(' ')?.[1] || '',
          image: account?.logo,
          isAccount: true
        }
      : { firstName, lastName, image };

  const { mappedPrivileges } = usePrivileges();
  const myPrivileges = useContext(PrivilegesContext);
  const myPrivilegeType = getMyPrivilegeType(myPrivileges, mappedPrivileges);

  const accountIsHost = account?.isHost;

  // Make sure to display the popover only if it is a freemium account and only on the sidebar (type is user)
  const shouldDisplayPopover = (accountIsHost || isAccountNetworkAccessSupplier) && type === 'user';

  const redirectToLandingPage = () => window.open('https://www.ubeya.com/lp/temporary-workforce-management', '_blank');

  return (
    <Wrapper className={className}>
      <DropDown
        usePortal
        showArrow={false}
        openFromRight={!!isMobile || !!isTablet}
        menuMinWidth={200}
        menuOffset={
          isTablet
            ? { top: 10, right: 25 }
            : isMobile
            ? { top: 10 }
            : isSideBar
            ? { top: -10, left: 0 }
            : { top: 15, ...(isRtl ? { left: 230 } : { left: -230 }) }
        }
        renderValue={
          <>
            {type === 'admin' && <AccountName>{account?.name}</AccountName>}
            <ProfileMenuAvatar {...primaryAvatar} />
          </>
        }>
        <DropDown.Item>
          <FlexColumn>
            <SmallBodySemiBold>
              {[firstName, lastName].join(' ')}
              <Privileges>, {myPrivilegeType}</Privileges>
            </SmallBodySemiBold>
            <Small>{email}</Small>
          </FlexColumn>
        </DropDown.Item>

        <Accounts>
          {accounts.map(({ id, name, logo }) => (
            <DropDown.Item
              key={id}
              onClick={() => (window.location = `/${id}`)}
              isVisible={accounts.length > 1}
              internalCss="padding: 0;">
              <AccountsContainer isSelected={accountId === id}>
                <Avatar
                  image={logo}
                  size="small"
                  isAccount
                  firstName={(name || '').split(' ')?.[0]}
                  lastName={(name || '').split(' ')?.[1]}
                />
                <span>{name}</span>
              </AccountsContainer>
            </DropDown.Item>
          ))}
        </Accounts>

        <DropDown.Separator isVisible={accounts.length > 1} />

        {isUbeyaAdmin && (
          <DropDown.Item
            onClick={() => setUbeyaAdminViewAsClient((prev) => !prev)}
            icon={ubeyaAdminViewAsClient && VSign}>
            {t('viewAsClient')}
          </DropDown.Item>
        )}

        <DropDown.Item
          icon={SubmoduleIcons.Reports}
          onClick={() => window.open(process.env.ORGANIZATION_URL, '_blank')}>
          {t('orgDashboard')}
        </DropDown.Item>

        {myPrivileges[privileges.BILLING] && (
          <DropDown.Item icon={SubmoduleIcons.Billing} onClick={() => push(billingTo)}>
            {t('billing')}
          </DropDown.Item>
        )}

        <DropDown.Item
          icon={ProductUpdatesIcon}
          onClick={() => window.open('https://learn.ubeya.com/en/collections/7405734-product-updates', '_blank')}>
          {t('productUpdates')}
        </DropDown.Item>

        {isHost && (
          <DropDown.Item icon={NotificationSettingsIcon} onClick={() => setIsHostProfileModal(true)}>
            {t('notificationSettings')}
          </DropDown.Item>
        )}

        <DropDown.Item icon={HelpCenterIcon} onClick={() => window.open('https://learn.ubeya.com', '_blank')}>
          {t('helpCenter')}
        </DropDown.Item>

        <DropDown.Item icon={SupportChatIcon} onClick={() => setOpenHelpCenter(true)}>
          {t('chatSupport')}
        </DropDown.Item>

        <DropDown.Item icon={LogoutIcon} onClick={() => logout('admin')}>
          {t('logout')}
        </DropDown.Item>
      </DropDown>

      {shouldDisplayPopover && (
        <Popover onClick={redirectToLandingPage}>
          <PoweredByText>{t('poweredByUbeya')}</PoweredByText>
          <PurpleLogo />
        </Popover>
      )}

      {isHostProfileModal && adminConfigData && (
        <ProfileModal
          update={updateAdminConfig}
          configInitialData={adminConfigData}
          onClose={() => setIsHostProfileModal(false)}
        />
      )}
    </Wrapper>
  );
};

export default ProfileMenu;
